import { useTranslation } from 'react-i18next';
import { useCycleStore } from '../../stores/cycleStore';
import { Card } from '../common';
import { useCurrency } from '../../lib/utils/currency';
import type { ProductionCycle } from '../../types';

interface ComparisonTableProps {
  selectedIds: string[];
}

export function ComparisonTable({ selectedIds }: ComparisonTableProps) {
  const { t } = useTranslation();
  const { symbol } = useCurrency();
  const { getCycle } = useCycleStore();
  
  const selectedCycles = selectedIds
    .map((id) => getCycle(id))
    .filter((cycle): cycle is ProductionCycle => cycle !== undefined);

  const formatCurrency = (value: number) => {
    return `${value.toLocaleString('tr-TR', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    })} ${symbol}`;
  };

  const formatNumber = (value: number, decimals: number = 2) => {
    return value.toLocaleString('tr-TR', {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    });
  };

  const getBestValue = (values: number[], higherIsBetter: boolean = true) => {
    if (values.length === 0) return null;
    return higherIsBetter ? Math.max(...values) : Math.min(...values);
  };

  const isHighlighted = (value: number, bestValue: number | null) => {
    return bestValue !== null && Math.abs(value - bestValue) < 0.001;
  };

  if (selectedCycles.length === 0) {
    return (
      <Card title={t('comparison.compare')}>
        <p className="text-gray-600 dark:text-gray-300 text-center py-8">
          {t('comparison.selectCycles')}
        </p>
      </Card>
    );
  }

  const fcrValues = selectedCycles.map((c) => c.performance.fcr);
  const epefValues = selectedCycles.map((c) => c.performance.epef);
  const survivalValues = selectedCycles.map((c) => c.performance.survivalRate);
  const profitValues = selectedCycles.map((c) => c.netProfit.netProfit);
  const revenueValues = selectedCycles.map((c) => c.revenue.grossRevenue);
  const expenseValues = selectedCycles.map((c) => c.netProfit.totalExpenses);

  const bestFCR = getBestValue(fcrValues, false); // Lower is better
  const bestEPEF = getBestValue(epefValues, true);
  const bestSurvival = getBestValue(survivalValues, true);
  const bestProfit = getBestValue(profitValues, true);
  const bestRevenue = getBestValue(revenueValues, true);
  const bestExpense = getBestValue(expenseValues, false); // Lower is better

  return (
    <Card title={t('comparison.comparisonTitle')}>
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
          <thead className="bg-gray-50 dark:bg-gray-800">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                {t('comparison.table')}
              </th>
              {selectedCycles.map((cycle) => (
                <th
                  key={cycle.id}
                  className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider"
                >
                  {cycle.name}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700">
            {/* Performance Metrics */}
            <tr className="bg-gray-50 dark:bg-gray-800">
              <td colSpan={selectedCycles.length + 1} className="px-4 py-2 text-sm font-semibold text-gray-900 dark:text-white">
                {t('comparison.performanceMetrics')}
              </td>
            </tr>
            <tr>
              <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">{t('results.fcr')}</td>
              {selectedCycles.map((cycle) => (
                <td
                  key={cycle.id}
                  className={`px-4 py-3 text-sm ${
                    isHighlighted(cycle.performance.fcr, bestFCR)
                      ? 'font-bold text-green-600 dark:text-green-400'
                      : 'text-gray-900 dark:text-white'
                  }`}
                >
                  {formatNumber(cycle.performance.fcr, 3)}
                </td>
              ))}
            </tr>
            <tr>
              <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">{t('results.epef')}</td>
              {selectedCycles.map((cycle) => (
                <td
                  key={cycle.id}
                  className={`px-4 py-3 text-sm ${
                    isHighlighted(cycle.performance.epef, bestEPEF)
                      ? 'font-bold text-green-600 dark:text-green-400'
                      : 'text-gray-900 dark:text-white'
                  }`}
                >
                  {formatNumber(cycle.performance.epef, 2)}
                </td>
              ))}
            </tr>
            <tr>
              <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">{t('comparison.survivalRate')}</td>
              {selectedCycles.map((cycle) => (
                <td
                  key={cycle.id}
                  className={`px-4 py-3 text-sm ${
                    isHighlighted(cycle.performance.survivalRate, bestSurvival)
                      ? 'font-bold text-green-600 dark:text-green-400'
                      : 'text-gray-900 dark:text-white'
                  }`}
                >
                  {formatNumber(cycle.performance.survivalRate, 2)}
                </td>
              ))}
            </tr>
            <tr>
              <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">{t('comparison.cycleDuration')}</td>
              {selectedCycles.map((cycle) => (
                <td key={cycle.id} className="px-4 py-3 text-sm text-gray-900 dark:text-white">
                  {cycle.performance.cycleDays}
                </td>
              ))}
            </tr>

            {/* Financial Metrics */}
            <tr className="bg-gray-50 dark:bg-gray-800">
              <td colSpan={selectedCycles.length + 1} className="px-4 py-2 text-sm font-semibold text-gray-900 dark:text-white">
                {t('comparison.financialMetrics')}
              </td>
            </tr>
            <tr>
              <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">{t('comparison.grossRevenue')}</td>
              {selectedCycles.map((cycle) => (
                <td
                  key={cycle.id}
                  className={`px-4 py-3 text-sm ${
                    isHighlighted(cycle.revenue.grossRevenue, bestRevenue)
                      ? 'font-bold text-green-600 dark:text-green-400'
                      : 'text-gray-900 dark:text-white'
                  }`}
                >
                  {formatCurrency(cycle.revenue.grossRevenue)}
                </td>
              ))}
            </tr>
            <tr>
              <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">{t('comparison.totalExpenses')}</td>
              {selectedCycles.map((cycle) => (
                <td
                  key={cycle.id}
                  className={`px-4 py-3 text-sm ${
                    isHighlighted(cycle.netProfit.totalExpenses, bestExpense)
                      ? 'font-bold text-green-600 dark:text-green-400'
                      : 'text-gray-900 dark:text-white'
                  }`}
                >
                  {formatCurrency(cycle.netProfit.totalExpenses)}
                </td>
              ))}
            </tr>
            <tr>
              <td className="px-4 py-3 text-sm font-semibold text-gray-900 dark:text-white">{t('comparison.netProfit')}</td>
              {selectedCycles.map((cycle) => (
                <td
                  key={cycle.id}
                  className={`px-4 py-3 text-sm font-semibold ${
                    isHighlighted(cycle.netProfit.netProfit, bestProfit)
                      ? 'text-green-600 dark:text-green-400'
                      : cycle.netProfit.netProfit >= 0
                      ? 'text-green-600 dark:text-green-400'
                      : 'text-red-600 dark:text-red-400'
                  }`}
                >
                  {formatCurrency(cycle.netProfit.netProfit)}
                </td>
              ))}
            </tr>
          </tbody>
        </table>
      </div>
    </Card>
  );
}
